import type { SupStoredTransmute } from "@/store/types";

import React, { Fragment, useMemo } from "react";

import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import Web3CommonSwitchNetwork from "@/components/Web3/Common/SwitchNetwork";
import useChainInfo from "@/hooks/useChainInfo";
import UiChipVault from "@/components/Ui/Chip/Vault";
import Web3TransmuteTx from "@/components/Web3/Transmute/Tx";
import { FixedNumber } from "@ethersproject/bignumber";
import { SupAssetLabel, SupButton, SupText } from "@repo/core-ui";
import { SupMint, SupWithdraw } from "@repo/core-ui";
import { SupAmountInput } from "@repo/ui-widgets";
import config from "@/../config/dapp.config";
import dictionaryConfig from "@/../config/dictionary.config";
import Link from "next/link";
import { parseUnits } from "viem";
import UiLabelChainScanLink from "@/components/Ui/Label/ChainScanLink";

const inputConfig = {
  fiatMaxDecimals: config.decimalsRoundingLimit,
  tokenMaxDecimals: config.decimalsBigRoundingLimit,
};

interface Web3TransmuteMainProps {
  balanceObject: undefined | { symbol: string; amount: string; price: number; decimals: number; icon: string };
  transmuteTarget: SupStoredTransmute;
  value: string;
  onChange: (value?: string | undefined) => void;
  price: number;
  onCancel: () => void;
  decimals: number;
  onFinished: () => void;
}

export default function Web3TransmuteMain(props: Web3TransmuteMainProps) {
  const { network } = useDynamicContext();

  const { transmuteTarget, onCancel } = props;

  const chainId = transmuteTarget.superposition.chain_id;

  const { data: chainData } = useChainInfo(chainId);

  const headerContent = useMemo(() => {
    switch (transmuteTarget.source) {
      case "withdraw":
        return {
          title: "Withdraw Assets",
          useWithdrawIcon: true,
        };
      case "deposit":
        return {
          title: "Enable Rewards",
          useWithdrawIcon: false,
        };
      default:
        return {
          title: "Transmute",
          useWithdrawIcon: false,
        };
    }
  }, [transmuteTarget]);

  const requireSwitchChain = useMemo(() => {
    return network ? parseInt(`${network}`, 10) !== chainId : false;
  }, [chainId, network]);

  return (
    <Fragment>
      <div className="space-y-4">
        {chainData && props.balanceObject && transmuteTarget.source === "direct" && (
          <SupAmountInput
            className="w-full"
            label="Transmute amount"
            value={props.value}
            balance={props.balanceObject}
            chain={chainData}
            config={inputConfig}
            sliderMarks={25}
            onChange={props.onChange}
          />
        )}
        <div className="inline-flex h-fit w-full flex-col items-start justify-start space-y-4 border-[.5px] border-neutral-100 bg-neutral-25 p-4">
          <div className="flex w-full items-center justify-between">
            <SupAssetLabel
              className="w-fit items-center gap-2"
              label={
                <SupText variant="caption" weight="bold" className="text-neutral-700">
                  {headerContent.title}
                </SupText>
              }
            >
              {headerContent.useWithdrawIcon ? <SupWithdraw className="h-4 w-4 text-neutral-900" /> : <SupMint className="h-4 w-4 text-neutral-900" />}
            </SupAssetLabel>
          </div>
          <div className="flex h-14 flex-col items-start justify-start gap-1 self-stretch">
            <div className="inline-flex items-center justify-between self-stretch py-1.5">
              <UiChipVault singleVault={transmuteTarget.superposition.vault} chainId={chainId} />

              <div className="flex items-center justify-end gap-2">
                <div className="flex items-center justify-end gap-2">
                  <div className="inline-flex flex-col items-end justify-start">
                    <div className="inline-flex h-5 items-center justify-start gap-1">
                      {props.value && (
                        <SupText variant="button" weight="bold" className="whitespace-nowrap text-right">
                          {parseFloat(props.value).toFixed(config.decimalsRoundingLimit)}
                          <span className="ml-0.5 inline-flex">{transmuteTarget.superposition.vault.symbol.replace("SUP-", "")}</span>
                        </SupText>
                      )}
                    </div>
                    <div className="inline-flex h-5 items-center justify-start gap-1">
                      <SupText variant="button" weight="bold">
                        $
                        {FixedNumber.from(`${parseFloat(props.value ?? "0") * props.price}`)
                          .round(config.decimalsRoundingLimit)
                          .toUnsafeFloat()
                          .toFixed(config.decimalsRoundingLimit)}
                      </SupText>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {["direct", "deposit"].includes(transmuteTarget.source) ? (
            <SupText variant="caption" className="space-y-3 self-stretch normal-case text-neutral-700" as="div">
              <p>Transmuting allows you to convert your ERC-1155 SuperPosition into an ERC-20 SuperPosition (or the other way around).</p>

              <p>You can transmute in either direction at any time.</p>

              <p>If you are intending to deposit your position into a SuperPool on Uniswap, Curve, or Balancer, you will need to transmute to ERC-20.</p>
            </SupText>
          ) : (
            <SupText variant="caption" className="space-y-3 self-stretch normal-case text-neutral-700" as="div">
              <p>Once the transmute transaction is done, you will submit another transaction to withdraw your assets.</p>
            </SupText>
          )}

          {transmuteTarget.txLink && (
            <div className="-mx-2">
              <UiLabelChainScanLink address={transmuteTarget.txLink} chain={Number(transmuteTarget.superposition.chain_id)} />
            </div>
          )}

          {transmuteTarget.source !== "withdraw" && (
            <Link href={dictionaryConfig.superPools.helpArticle} className="text-neutral-700 underline hover:no-underline" target="_blank">
              <SupText variant="caption" weight="bold" className="self-stretch ">
                Learn More
              </SupText>
            </Link>
          )}
        </div>
      </div>
      <div className="mt-auto flex w-full flex-col items-stretch justify-between space-y-4">
        <SupButton fluid onClick={onCancel} className="hover:opacity-80" variant="outlined" color="primary">
          Cancel
        </SupButton>
        {requireSwitchChain ? (
          <Web3CommonSwitchNetwork targetChainId={parseInt(`${chainId}`)} />
        ) : (
          <Web3TransmuteTx
            superPosition={transmuteTarget.superposition}
            amount={props.value ? parseUnits(props.value, props.decimals).toString() : undefined}
            type={transmuteTarget.direction}
            onFinished={props.onFinished}
            name={transmuteTarget.source === "deposit" ? "Enable rewards" : undefined}
          />
        )}
      </div>
    </Fragment>
  );
}
